import { ReactElement, useState } from 'react';
import {
  HiChevronRight,
  HiOutlineInformationCircle,
  HiOutlinePencil,
} from 'react-icons/hi2';
import FormEntry from '../../components/FormEntry';
import {
  GroundSpeedSetup,
  MagnetDetailsEntry,
  SPEED_POSITION,
  SpeedSensorEntry,
} from '../../types/CalibrationParameters';
import {
  LegacySpeedFields,
  SpeedSource,
  changeSensorId,
  effectivePrimaryId,
  effectiveSetup,
  effectiveSensorSpacing,
  removeSensorSpacing,
  sensorIsFront,
  sensorOffBike,
  setPrimarySensor,
  setSensorLocation,
  setSensorOffBike,
  setSpeedSource,
  speedSource,
} from './groundSpeedUtils';

type Mode = 'manual' | 'inherit' | 'automatic';

interface GroundSpeedSectionProps {
  // The form's ground_speed_setup value (null/undefined = legacy config).
  value: GroundSpeedSetup | null | undefined;
  // Companion columns: magnet_details is the LIVE promoted-spacing
  // calibration (overlaid onto the roster for display); the rest are
  // superseded read-fallback sources for legacy configs.
  legacy: LegacySpeedFields;
  mode: Mode;
  isEditing: boolean;
  modeOptions: { value: string; label: string }[];
  inheritedValue: GroundSpeedSetup | null | undefined;
  parentDescription?: string | null;
  magnetPrivilege: boolean;
  info?: string;
  activeTooltip: string | null;
  setActiveTooltip: (name: string | null) => void;
  onToggleEdit: () => void;
  onModeChange: (mode: Mode) => void;
  // Every edit hands back the WHOLE object (write-object, read-fallback).
  onChange: (next: GroundSpeedSetup) => void;
}

const SOURCE_OPTIONS: { value: SpeedSource; label: string }[] = [
  { value: 'front', label: 'Front Wheel' },
  { value: 'rear', label: 'Rear Wheel' },
  { value: 'gps', label: 'GPS' },
];

const FIELD = 'ground_speed_setup';

function formatSpacing(entry: MagnetDetailsEntry): string {
  const angles = (entry.angles_deg || []).map((a) => `${a.toFixed(1)}°`).join(' / ');
  const parts = [`${entry.magnet_count} magnets`, angles];
  if (entry.quality !== undefined && entry.quality !== null) {
    parts.push(`q ${entry.quality.toFixed(2)}`);
  }
  if (entry.n_windows) parts.push(`${entry.n_windows} windows`);
  if (entry.calibrated_at) parts.push(entry.calibrated_at.slice(0, 10));
  return parts.join(' · ');
}

export default function GroundSpeedSection(props: GroundSpeedSectionProps): ReactElement {
  const {
    value, legacy, mode, isEditing, modeOptions, inheritedValue, parentDescription,
    magnetPrivilege, info, activeTooltip, setActiveTooltip, onToggleEdit, onModeChange, onChange,
  } = props;
  const [expanded, setExpanded] = useState(false);

  const isInherit = mode === 'inherit';
  // Inherit shows the parent's object; otherwise the stored object with the
  // legacy-columns fallback behind it.
  const setup = isInherit && inheritedValue
    ? inheritedValue
    : effectiveSetup(value, legacy);
  const sensors = setup.sensors || [];
  const source = speedSource(setup);
  // Structure (source, locations, primary, ids, magnet count) is editable in
  // both Manual and Automatic — the modes differ only in who owns the spacing
  // values: Automatic hands them to autocal (Clear forces a recalibration),
  // Manual freezes them as stored. Inherit locks the whole object.
  const canEdit = isEditing && mode !== 'inherit';

  const modeLabel = (() => {
    if (mode === 'inherit' && parentDescription) return `Inherit (from: ${parentDescription})`;
    if (mode === 'inherit') return 'Inherit';
    if (mode === 'automatic') return 'Automatic';
    return 'Manual';
  })();
  const modeClass = mode === 'manual'
    ? 'cr-bg-green-100'
    : mode === 'automatic' ? 'cr-bg-blue-100' : 'cr-bg-gray-100';

  const sourceLabel = SOURCE_OPTIONS.find((o) => o.value === source)?.label ?? 'Front Wheel';
  const effPrimaryId = effectivePrimaryId(setup, source);
  const primaryUnresolved = source !== 'gps' && effPrimaryId == null && sensors.length > 0;

  const emit = (next: GroundSpeedSetup) => onChange(next);

  // With a sole sensor and a wheel source, the Speed Source radio decides the
  // sensor's position — its own location radios are read-only.
  const soleLocked = sensors.length === 1 && source !== 'gps';

  const primarySensor = sensors.find((s) => s.id_ === effPrimaryId);

  const renderSensorRow = (sensor: SpeedSensorEntry) => {
    const isPrimary = sensor.id_ === effPrimaryId;
    // An UNKNOWN position is not a decision: render both Location radios
    // unchecked with a 'not set' hint rather than a checked default.
    const positionKnown = soleLocked
      || (sensor.position != null && sensor.position !== SPEED_POSITION.UNKNOWN);
    const front = soleLocked ? source === 'front' : sensorIsFront(sensor.position);
    const offBike = sensorOffBike(sensor.position);
    // Offer 'Set primary' when this on-bike sensor could take over: several
    // sensors share the primary's side, or the primary is unresolved and this
    // sensor sits on the speed-source side.
    const sharesPrimarySide = primarySensor !== undefined
      && !sensorOffBike(primarySensor.position)
      && sensorIsFront(primarySensor.position) === front
      && sensors.filter((s) => !sensorOffBike(s.position) && sensorIsFront(s.position) === front).length > 1;
    const couldResolve = effPrimaryId == null && source !== 'gps' && front === (source === 'front');
    const canSetPrimary = canEdit && !isPrimary && !offBike && (sharesPrimarySide || couldResolve);
    return (
      <div key={sensor.id_} className="cr-flex cr-flex-col cr-gap-1 cr-rounded cr-border cr-border-gray-200 cr-p-2">
        <div className="cr-flex cr-items-center cr-gap-3">
          <span className="cr-text-sm cr-text-gray-600">ID</span>
          {canEdit ? (
            <input
              type="number"
              value={sensor.id_ || ''}
              placeholder="..."
              onChange={(e) => {
                const next = Number(e.target.value);
                if (Number.isFinite(next)) emit(changeSensorId(setup, sensor.id_, next));
              }}
              className="cr-w-24 cr-rounded cr-border cr-border-gray-300 cr-px-2 cr-py-1 cr-text-sm"
            />
          ) : (
            <span className="cr-text-sm cr-text-gray-900">{sensor.id_ || '...'}</span>
          )}
          <span
            className={'cr-rounded cr-px-2 cr-py-0.5 cr-text-xs cr-font-medium '
              + (isPrimary ? 'cr-bg-green-100 cr-text-green-800' : 'cr-bg-gray-100 cr-text-gray-600')}
          >
            {isPrimary ? 'Primary' : 'Secondary'}
          </span>
          {canSetPrimary && (
            <button
              type="button"
              onClick={() => emit(setPrimarySensor(setup, sensor.id_))}
              className="cr-ml-auto cr-rounded cr-border cr-border-gray-300 cr-px-2 cr-py-0.5 cr-text-xs
                cr-text-gray-700 hover:cr-bg-gray-100"
              title="Make this the primary sensor for speed"
            >
              Set primary
            </button>
          )}
        </div>
        <div className="cr-flex cr-flex-wrap cr-items-center cr-gap-4 cr-pl-6">
          <span
            className="cr-flex cr-items-center cr-gap-2 cr-text-sm cr-text-gray-600"
            title={soleLocked ? 'With one sensor, the Speed Source sets its location' : undefined}
          >
            Location:
            {[{ front: true, label: 'Front' }, { front: false, label: 'Rear' }].map(({ front: f, label }) => (
              <label key={label} className="cr-flex cr-items-center cr-gap-1">
                <input
                  type="radio"
                  checked={positionKnown && front === f}
                  disabled={!canEdit || soleLocked}
                  onChange={() => emit(setSensorLocation(setup, sensor.id_, f, source))}
                />
                <span className="cr-text-sm cr-text-gray-900">{label}</span>
              </label>
            ))}
            {!positionKnown && <span className="cr-text-xs cr-text-amber-600">not set</span>}
          </span>
          <label className="cr-flex cr-items-center cr-gap-1 cr-text-sm cr-text-gray-600"
            title="Wheel driven by a trainer roller — off for the primary sensor">
            <input
              type="checkbox"
              checked={offBike}
              disabled={!canEdit || isPrimary}
              onChange={(e) => emit(setSensorOffBike(setup, sensor.id_, e.target.checked, source))}
            />
            Off-Bike
          </label>
          <span className="cr-text-sm cr-text-gray-600">
            Instance: <span className="cr-text-gray-900">{sensor.session_instance ?? 0}</span>
          </span>
          {magnetPrivilege && (
            <span className="cr-flex cr-items-center cr-gap-1 cr-text-sm cr-text-gray-600">
              Magnet Count:
              {canEdit ? (
                <input
                  type="number"
                  min={1}
                  value={sensor.magnet_count ?? 1}
                  onChange={(e) => {
                    const next = Math.max(1, Number(e.target.value) || 1);
                    emit({
                      ...setup,
                      sensors: sensors.map((s) => (s.id_ === sensor.id_ ? { ...s, magnet_count: next } : s)),
                    });
                  }}
                  className="cr-w-16 cr-rounded cr-border cr-border-gray-300 cr-px-2 cr-py-0.5 cr-text-sm"
                />
              ) : (
                <span className="cr-text-gray-900">{sensor.magnet_count ?? 1}</span>
              )}
            </span>
          )}
        </div>
        {magnetPrivilege && (sensor.magnet_count ?? 1) > 1 && (() => {
          // Promoted spacing lives in the magnet_details column now (the
          // roster object no longer receives fits); embedded spacing from
          // the branch era fills gaps. Column entries are autocal-owned,
          // so Clear only offers on embedded (client-editable) values.
          const eff = effectiveSensorSpacing(sensor, legacy?.magnet_details);
          return (
            <div className="cr-flex cr-items-center cr-gap-2 cr-pl-6">
              <span className="cr-text-sm cr-text-gray-600">Magnet Spacing:</span>
              {eff ? (
                <>
                  <span className="cr-text-sm cr-text-gray-900">
                    {formatSpacing(eff.spacing)}
                    {eff.promoted ? ' · promoted' : ''}
                  </span>
                  {canEdit && !eff.promoted && (
                    <button
                      type="button"
                      onClick={() => emit(removeSensorSpacing(setup, sensor.id_))}
                      className="cr-rounded cr-px-2 cr-py-0.5 cr-text-xs cr-text-red-600 hover:cr-bg-red-50"
                      title="Clear this wheel's calibration (recalibrates on the next good run)"
                    >
                      Clear
                    </button>
                  )}
                </>
              ) : (
                <span className="cr-text-sm cr-text-gray-500">
                  {mode === 'automatic' ? 'Not calibrated — fills from the next good run' : 'Not calibrated'}
                </span>
              )}
            </div>
          );
        })()}
      </div>
    );
  };

  return (
    <>
      <FormEntry label="Speed Source">
        <div className="cr-flex cr-items-center cr-gap-2">
          <button
            type="button"
            onClick={onToggleEdit}
            className="cr-rounded cr-p-1 hover:cr-bg-gray-100"
            title="Edit"
          >
            <HiOutlinePencil className="cr-h-5 cr-w-5 cr-text-gray-600" />
          </button>
          {isEditing ? (
            <>
              <select
                value={mode}
                onChange={(e) => onModeChange(e.target.value as Mode)}
                className="cr-min-w-[120px] cr-rounded cr-border cr-border-gray-300 cr-px-2 cr-py-1 cr-text-sm"
              >
                {modeOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              {mode !== 'inherit' ? (
                <div className="cr-flex cr-flex-row cr-gap-4">
                  {SOURCE_OPTIONS.map(({ value: v, label }) => (
                    <label key={v} className="cr-flex cr-items-center cr-gap-2">
                      <input
                        type="radio"
                        checked={source === v}
                        onChange={() => emit(setSpeedSource(effectiveSetup(value, legacy), v))}
                        className="cr-mr-2"
                      />
                      <span className="cr-text-sm">{label}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <span className="cr-text-sm cr-text-gray-600">{sourceLabel}</span>
              )}
            </>
          ) : (
            <>
              <span
                className={'cr-min-w-[120px] cr-rounded cr-px-2 cr-py-1 cr-text-sm cr-font-medium '
                  + `cr-text-gray-700 ${modeClass}`}
              >
                {modeLabel}
              </span>
              <span className="cr-text-sm cr-text-gray-900">{sourceLabel}</span>
            </>
          )}
          {primaryUnresolved && (
            <span className="cr-text-sm cr-text-amber-600" title="No sensor holds the selected position yet">
              ...
            </span>
          )}
          {info && (
            <div className="cr-relative">
              <button
                type="button"
                onMouseEnter={() => setActiveTooltip(FIELD)}
                onMouseLeave={() => setActiveTooltip(null)}
                className="cr-rounded cr-p-1 hover:cr-bg-gray-100"
                title={info}
              >
                <HiOutlineInformationCircle className="cr-h-4 cr-w-4 cr-text-blue-500" />
              </button>
              {activeTooltip === FIELD && (
                <div
                  className={'cr-absolute cr-bottom-full cr-right-0 cr-z-10 cr-mb-2 cr-w-64 cr-rounded '
                    + 'cr-bg-gray-800 cr-px-3 cr-py-2 cr-text-sm cr-text-white cr-shadow-lg'}
                >
                  {info}
                </div>
              )}
            </div>
          )}
        </div>
      </FormEntry>
      <div className="cr-flex cr-flex-col cr-gap-2 cr-pl-2">
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="cr-flex cr-items-center cr-gap-1 cr-text-sm cr-font-medium cr-text-gray-700"
        >
          <HiChevronRight
            className={`cr-h-4 cr-w-4 cr-transition-transform ${expanded ? 'cr-rotate-90' : ''}`}
          />
          {`Sensors (${sensors.length})`}
        </button>
        {expanded && (
          <div className="cr-flex cr-flex-col cr-gap-2 cr-pl-5">
            {sensors.length === 0 && (
              <span className="cr-text-sm cr-text-gray-500">No speed sensors recorded</span>
            )}
            {sensors.map(renderSensorRow)}
          </div>
        )}
      </div>
    </>
  );
}
